import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { getStaticPageMetadata } from './pageMetadata.js';

const PageMetaContext = createContext({ meta: null, setPageMeta: () => {} });

export function PageMetaProvider({ children }) {
  const location = useLocation();
  const [override, setOverride] = useState(null);

  // Note: do not clear `override` here on route change. React runs child
  // effects before parent effects on mount, so a path-change-keyed clear in
  // this provider would race with the new route's usePageMetadata() set and
  // leave the topbar blank on dynamic routes. The route transition itself is
  // already handled: the outgoing page's usePageMetadata cleanup fires on
  // unmount and nulls the override before the new page mounts.

  const meta = useMemo(
    () => override || getStaticPageMetadata(location.pathname),
    [override, location.pathname],
  );

  const setPageMeta = useCallback((next) => {
    setOverride(next);
  }, []);

  const value = useMemo(() => ({ meta, setPageMeta }), [meta, setPageMeta]);
  return <PageMetaContext.Provider value={value}>{children}</PageMetaContext.Provider>;
}

export function usePageMeta() {
  return useContext(PageMetaContext);
}

// Hook for dynamic pages (portfolio profile, project profile, gate review, etc.)
// to override the static map at render time. Passing null clears any override.
export function usePageMetadata({ title, subtitle, kicker } = {}) {
  const { setPageMeta } = useContext(PageMetaContext);
  useEffect(() => {
    if (title || subtitle || kicker) {
      setPageMeta({ title, subtitle, kicker });
    }
    return () => setPageMeta(null);
  }, [title, subtitle, kicker, setPageMeta]);
}
